import type { MaybeRefOrGetter } from 'vue';
import { ref, toValue } from 'vue';

export interface Point {
	x: number;
	y: number;
}

export type SubmenuSide = 'left' | 'right';

export interface UseSubmenuSafePathOptions {
	submenuElement: MaybeRefOrGetter<HTMLElement | null | undefined>;
	side: MaybeRefOrGetter<SubmenuSide>;
}

/**
 * Tracks pointer movement from a parent menu item toward an open submenu.
 *
 * While the pointer stays inside the triangle between the last known pointer
 * position and the submenu's near edge, callers can keep the parent menu open
 * instead of treating the pointer as having intentionally left the item.
 */
export function useSubmenuSafePath(options: UseSubmenuSafePathOptions) {
	const lastPointerPosition = ref<Point | null>(null);

	/** Stores the latest pointer position as the triangle origin for future leave checks. */
	function handlePointerMove(event: MouseEvent) {
		lastPointerPosition.value = getEventPoint(event);
	}

	/** Returns whether the pointer is still traveling through the submenu safe triangle. */
	function isWithinSafePath(event: MouseEvent): boolean {
		const origin = lastPointerPosition.value;
		const submenuElement = toValue(options.submenuElement);

		if (!origin || !submenuElement) return false;

		return isPointInSubmenuSafePath(
			getEventPoint(event),
			origin,
			submenuElement.getBoundingClientRect(),
			toValue(options.side),
		);
	}

	/** Clears the current triangle origin when the submenu path is no longer relevant. */
	function resetSafePath() {
		lastPointerPosition.value = null;
	}

	return {
		handlePointerMove,
		isWithinSafePath,
		resetSafePath,
	};
}

/**
 * Checks whether `point` sits inside the triangle from `origin` to the submenu's near edge.
 *
 * The submenu can open on either side, and collision handling may flip it at runtime,
 * so the near edge is resolved from the actual submenu rect before falling back to the
 * preferred side.
 */
export function isPointInSubmenuSafePath(point: Point, origin: Point, submenuRect: DOMRect, side: SubmenuSide): boolean {
	const submenuEdgeX = getSubmenuSafePathEdgeX(origin, submenuRect, side);

	return isPointInTriangle(
		point,
		origin,
		{ x: submenuEdgeX, y: submenuRect.top },
		{ x: submenuEdgeX, y: submenuRect.bottom },
	);
}

function getEventPoint(event: MouseEvent): Point {
	return { x: event.clientX, y: event.clientY };
}

/**
 * Resolves the submenu edge that should form the far side of the safe triangle.
 *
 * Pointer origins outside the submenu bounds indicate the actual rendered side,
 * which is more reliable than the preferred side when the popover has flipped.
 */
function getSubmenuSafePathEdgeX(origin: Point, submenuRect: DOMRect, preferredSide: SubmenuSide): number {
	if (origin.x <= submenuRect.left) return submenuRect.left;
	if (origin.x >= submenuRect.right) return submenuRect.right;

	return preferredSide === 'right' ? submenuRect.left : submenuRect.right;
}

/** Uses signed edge tests to determine whether a point is inside a triangle. */
function isPointInTriangle(point: Point, a: Point, b: Point, c: Point): boolean {
	const d1 = sign(point, a, b);
	const d2 = sign(point, b, c);
	const d3 = sign(point, c, a);

	const hasNegative = d1 < 0 || d2 < 0 || d3 < 0;
	const hasPositive = d1 > 0 || d2 > 0 || d3 > 0;

	return !(hasNegative && hasPositive);
}

/** Returns the signed area orientation for the point relative to a triangle edge. */
function sign(p1: Point, p2: Point, p3: Point): number {
	return (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y);
}
